import React from 'react';
import { Store, Award, Sparkles, ChevronUp, ChevronDown, BellOff, Bell, LogOut, ChevronRight, Instagram, MapPin } from 'lucide-react';
import { calculateLoyaltyLevel } from '../../utils/loyalty';

const GlobalBusinessList = ({
    businesses,
    activeTab,
    businessPrefs,
    toggleNotifications,
    moveBusiness,
    handleBusinessExit,
    setShowLoginModal,
    handleGlobalLogout,
    t,
    navigate,
    primaryColor,
    selectedFilter,
    toggleFavorite,
    autoJoinAndNavigate,
    setBusinessPrefs
}) => {
    // Utility to check if business info is open
    const isBusinessOpen = (workingHours = []) => {
        if (!workingHours || workingHours.length === 0) return null;

        const days = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
        const now = new Date();
        const nowMinutes = now.getHours() * 60 + now.getMinutes();

        // Bugünün saatlerini kontrol et
        const currentDayName = days[now.getDay()];
        const dayInfo = workingHours.find(wh => wh.day === currentDayName);

        if (dayInfo && dayInfo.active && dayInfo.open && dayInfo.close) {
            const [openH, openM] = dayInfo.open.split(':').map(Number);
            const [closeH, closeM] = dayInfo.close.split(':').map(Number);
            const openMinutes = openH * 60 + openM;
            const closeMinutes = closeH * 60 + closeM;

            if (closeMinutes < openMinutes) {
                // Gece yarısını geçen saat — bugünün açılışından sonrası
                if (nowMinutes >= openMinutes) return true;
            } else {
                if (nowMinutes >= openMinutes && nowMinutes < closeMinutes) return true;
            }
        }

        // Dünün gece yarısı geçişini kontrol et (örn. Ptesi 08:00-04:00, şu an Salı 02:00)
        const yesterdayIdx = (now.getDay() + 6) % 7;
        const yesterdayName = days[yesterdayIdx];
        const yesterdayInfo = workingHours.find(wh => wh.day === yesterdayName);

        if (yesterdayInfo && yesterdayInfo.active && yesterdayInfo.open && yesterdayInfo.close) {
            const [oH, oM] = yesterdayInfo.open.split(':').map(Number);
            const [cH, cM] = yesterdayInfo.close.split(':').map(Number);
            const oMin = oH * 60 + oM;
            const cMin = cH * 60 + cM;

            if (cMin < oMin && nowMinutes < cMin) return true;
        }

        return !dayInfo || !dayInfo.active ? false : false;
    };

    return (
        <>
            {(businesses.length === 0 && activeTab !== 'settings') ? (
                <div className="card glass-premium" style={{
                    padding: '4rem 2rem',
                    textAlign: 'center',
                    borderRadius: '24px'
                }}>
                    <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(255,255,255,0.03)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
                        <Store size={40} style={{ opacity: 0.2 }} />
                    </div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 800, marginBottom: '0.75rem' }}>
                        {selectedFilter === 'favorites' ? 'Henüz Favoriniz Yok' :
                                selectedFilter === 'nearby' ? 'Yakın İşletme Yok' :
                                    t('portal.noBusinesses')}
                    </h3>
                    <p style={{ color: '#a69088', fontSize: '0.875rem', marginBottom: '2rem', lineHeight: 1.6 }}>
                        {selectedFilter === 'favorites' ? 'En sevdiğiniz işletmeleri kalp simgesine basarak buraya ekleyebilirsiniz.' :
                            selectedFilter === 'nearby' ? 'Şu an yakın çevrenizde kayıtlı bir işletme bulunmuyor.' :
                                t('portal.noBusinessesDesc', 'Henüz bir işletmeye kayıtlı değilsiniz. İşletmelerde QR kodunuzu okutarak puan biriktirmeye başlayabilirsiniz.')}
                    </p>
                    <button
                        onClick={() => setShowLoginModal(true)}
                        className="ai-glow-button"
                        style={{
                            background: primaryColor,
                            color: 'white',
                            padding: '14px 28px',
                            borderRadius: '16px',
                            border: 'none',
                            fontWeight: 800,
                            fontSize: '0.9375rem',
                            boxShadow: `0 10px 20px -5px ${primaryColor}50`
                        }}
                    >
                        {t('portal.restoreNow', 'Kayıtlı Puanlarımı Bul')}
                    </button>
                </div>
            ) : (
                businesses.map((biz, index) => {
                    const isVisited = !!biz.customer;
                    return activeTab === 'settings' ? (
                        isVisited && (
                            <div 
                                key={biz.id} 
                                style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    padding: '14px 20px', 
                                    background: '#ffffff', 
                                    borderRadius: '20px', 
                                    marginBottom: '12px', 
                                    border: '1px solid #f1f5f9',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                                    transition: 'all 0.2s ease',
                                    animation: 'fadeIn 0.4s ease-out forwards'
                                }}
                            >
                                <div style={{ 
                                    width: '44px', 
                                    height: '44px', 
                                    borderRadius: '12px', 
                                    overflow: 'hidden', 
                                    background: '#f8fafc', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center',
                                    border: '1px solid #f1f5f9',
                                    flexShrink: 0
                                }}>
                                    {biz.tenant.theme?.logoUrl ? (
                                        <img src={biz.tenant.theme.logoUrl} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                    ) : (
                                        <Store color="#a69088" size={22} />
                                    )}
                                </div>
                                <div style={{ flex: 1, marginLeft: '16px', minWidth: 0 }}>
                                    <h3 style={{ fontSize: '1rem', fontWeight: 800, color: '#1a1412', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{biz.tenant.name}</h3>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '2px' }}>
                                        <span style={{ fontSize: '0.8125rem', color: '#a69088', fontWeight: 700 }}>{biz.customer?.points || 0} {t('portal.points')}</span>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                    <button onClick={() => toggleNotifications(biz.id)} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}>
                                        {businessPrefs[biz.id]?.notificationsDisabled ? <BellOff size={20} color="#a69088" style={{ opacity: 0.5 }} /> : <Bell size={20} color="#a69088" fill="#a69088" />}
                                    </button>
                                    <button onClick={() => handleBusinessExit(biz.id, biz.tenant.name)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#ef4444' }}>
                                        <span style={{ fontSize: '22px', fontWeight: 'bold' }}>✖</span>
                                    </button>
                                </div>
                            </div>
                        )
                    ) : (
                        <div
                            key={biz.id}
                            className="business-card-premium fade-in"
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                cursor: 'pointer',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                borderRadius: '24px',
                                background: '#ffffff',
                                border: '1px solid rgba(0,0,0,0.05)',
                                boxShadow: '0 10px 30px -10px rgba(0,0,0,0.08)',
                                overflow: 'hidden',
                                position: 'relative',
                                marginBottom: '20px'
                            }}
                            onClick={() => {
                                if (isVisited) {
                                    // If the business was hidden (exited), un-hide it when re-entering
                                    if (businessPrefs[biz.id]?.hidden) {
                                        setBusinessPrefs(prev => {
                                            const updated = { ...prev, [biz.id]: { ...prev[biz.id], hidden: false } };
                                            localStorage.setItem('crm_customer_prefs', JSON.stringify(updated));
                                            return updated;
                                        });
                                    }
                                    // Already a member – go directly to portal
                                    navigate(`/portal/${biz.tenant.slug || biz.id}`);
                                } else {
                                    // Not a member yet – auto-join then navigate
                                    if (autoJoinAndNavigate) {
                                        autoJoinAndNavigate(biz.id, biz.tenant.slug);
                                    } else {
                                        navigate(`/portal/${biz.tenant.slug || biz.id}`);
                                    }
                                }
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-6px)';
                                e.currentTarget.style.boxShadow = `0 20px 40px -12px rgba(0,0,0,0.12)`;
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 10px 30px -10px rgba(0,0,0,0.08)';
                            }}
                        >
                            <div style={{ height: '130px', background: biz.tenant.bannerUrl || biz.tenant.banner_url ? `url(${biz.tenant.bannerUrl || biz.tenant.banner_url}) center/cover` : `linear-gradient(135deg, #4a2810 0%, var(--primary) 50%, #b08060 100%)`, position: 'relative' }}>
                                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent, rgba(0,0,0,0.1))' }}></div>
                                {/* Favorite Toggle */}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleFavorite(biz.id);
                                    }}
                                    style={{
                                        position: 'absolute',
                                        top: '12px',
                                        left: '12px',
                                        width: '36px',
                                        height: '36px',
                                        borderRadius: '12px',
                                        background: '#ffffff',
                                        border: 'none',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        zIndex: 15,
                                        boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    <span style={{ fontSize: '22px', color: businessPrefs[biz.id]?.favorite ? '#ff4b2b' : '#cbd5e1' }}>
                                        {businessPrefs[biz.id]?.favorite ? '♥' : '♡'}
                                    </span>
                                </button>

                                {/* Open/Closed Badge — on banner top right */}
                                {(() => {
                                    const wh = biz.tenant.workingHours || biz.tenant.working_hours;
                                    const openStatus = isBusinessOpen(wh);
                                    if (openStatus === null) return null;
                                    return (
                                        <div style={{
                                            position: 'absolute',
                                            top: '12px',
                                            right: '12px',
                                            padding: '4px 10px',
                                            borderRadius: '20px',
                                            fontSize: '0.65rem',
                                            fontWeight: 800,
                                            letterSpacing: '0.5px',
                                            textTransform: 'uppercase',
                                            background: openStatus ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.95)',
                                            color: openStatus ? '#16a34a' : '#dc2626',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                            zIndex: 15
                                        }}>
                                            {openStatus ? 'AÇIK' : 'KAPALI'}
                                        </div>
                                    );
                                })()}
                            </div>

                            <div style={{ padding: '38px 18px 18px', position: 'relative' }}>
                                {/* Logo */}
                                <div style={{ position: 'absolute', top: '-34px', left: '18px', width: '68px', height: '68px', background: '#ffffff', borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', boxShadow: '0 10px 24px rgba(0,0,0,0.14)', border: '3px solid #ffffff' }}>
                                    {biz.tenant.theme?.logoUrl ? <img src={biz.tenant.theme.logoUrl} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <Store color="var(--primary)" size={30} />}
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        {/* Row 1: Name + Social Icons */}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                            <h3 style={{ fontSize: '1.1rem', fontWeight: 900, margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{biz.tenant.name}</h3>
                                            {(biz.tenant.socialMedia?.instagram || biz.tenant.social_media?.instagram) && (
                                                <a
                                                    href={`https://instagram.com/${(biz.tenant.socialMedia?.instagram || biz.tenant.social_media?.instagram).replace('@', '')}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{
                                                        width: '24px', height: '24px', borderRadius: '7px',
                                                        background: 'linear-gradient(135deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        flexShrink: 0, transition: 'transform 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.15)'; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                                                >
                                                    <Instagram size={13} color="white" />
                                                </a>
                                            )}
                                            {(biz.tenant.address || biz.tenant.city || biz.tenant.googleMapsUrl || biz.tenant.google_maps_url) && (
                                                <a
                                                    href={
                                                        biz.tenant.googleMapsUrl || biz.tenant.google_maps_url ||
                                                        `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([biz.tenant.address, biz.tenant.city].filter(Boolean).join(', '))}`
                                                    }
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{
                                                        width: '24px', height: '24px', borderRadius: '7px',
                                                        background: 'rgba(59, 130, 246, 0.15)',
                                                        border: '1px solid rgba(59, 130, 246, 0.3)',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        flexShrink: 0, transition: 'transform 0.2s', cursor: 'pointer'
                                                    }}
                                                    title={[biz.tenant.address, biz.tenant.city].filter(Boolean).join(', ')}
                                                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.15)'; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                                                >
                                                    <MapPin size={13} color="#3b82f6" />
                                                </a>
                                            )}
                                        </div>

                                        {/* Row 2: Points + Loyalty Level badges */}
                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                            {isVisited ? (
                                                <>
                                                    <div style={{
                                                        display: 'flex', alignItems: 'center', gap: '5px',
                                                        fontSize: '0.8rem', fontWeight: 800,
                                                        background: 'rgba(111,78,55,0.1)',
                                                        color: 'var(--primary)',
                                                        padding: '5px 11px', borderRadius: '20px'
                                                    }}>
                                                        <Sparkles size={12} />
                                                        {biz.customer.points} Puan
                                                    </div>
                                                    {(() => {
                                                        const level = calculateLoyaltyLevel(biz.customer, biz.tenant);
                                                        if (!level) return null;
                                                        return (
                                                            <div style={{
                                                                display: 'flex', alignItems: 'center', gap: '5px',
                                                                fontSize: '0.8rem', fontWeight: 800,
                                                                background: `${level.color || '#94A3B8'}18`,
                                                                color: level.color || '#94A3B8',
                                                                padding: '5px 11px', borderRadius: '20px'
                                                            }}>
                                                                <Award size={12} />
                                                                {level.label}
                                                            </div>
                                                        );
                                                    })()}
                                                </>
                                            ) : (
                                                <div style={{
                                                    display: 'flex', alignItems: 'center', gap: '5px',
                                                    fontSize: '0.78rem', fontWeight: 700,
                                                    background: 'rgba(166,144,136,0.1)',
                                                    color: '#a69088',
                                                    padding: '5px 11px', borderRadius: '20px'
                                                }}>
                                                    <Sparkles size={12} />
                                                    Keşfetmeye Hazır
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <ChevronRight size={20} color="var(--primary)" style={{ flexShrink: 0, marginLeft: '8px', marginTop: '2px' }} />
                                </div>
                            </div>
                        </div>
                    );
                })
            )}

            {activeTab === 'settings' && (
                <div style={{ marginTop: '2rem', borderTop: '1px solid #f3f4f6', paddingTop: '2rem' }}>
                    <button
                        onClick={handleGlobalLogout}
                        style={{
                            width: '100%',
                            padding: '16px',
                            background: 'rgba(239, 68, 68, 0.05)',
                            color: '#ef4444',
                            border: '1px solid rgba(239, 68, 68, 0.1)',
                            borderRadius: '16px',
                            fontWeight: 800,
                            fontSize: '1rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '10px',
                            transition: 'all 0.2s',
                            cursor: 'pointer'
                        }}
                        onMouseOver={e => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
                        onMouseOut={e => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.05)'}
                    >
                        <LogOut size={20} />
                        Oturumu Kapat (Çıkış Yap)
                    </button>
                    <p style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.8125rem', color: '#a69088', lineHeight: 1.5 }}>
                        Bu buton aktif profil oturumunuzu sonlandırır ve sizi ana sayfaya yönlendirir.
                    </p>
                </div>
            )}
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .fade-in {
                    animation: fadeIn 0.4s ease-out forwards;
                }
            `}</style>
        </>
    );
};

export default GlobalBusinessList;
